import { CompanySearchResponse } from '../schemas/CompanySearchResponse.schema';
import { MetricsTransactionDataModel } from '../schemas/MetricsTransaction.schema';
import { Audit } from '../types';
import { ICurrencyDataModel } from './currency.data-model';
import { IFinanceRoundDataModel } from './finance-round.data-model';
import { IFundDataModel } from './fund.data-model';
import { IRoundDataModel } from './round.data-model';

export enum ProceedsTypes {
  Cash = 'Cash',
  StockProceeds = 'Stock Proceeds',
}

export enum CouponType {
  Simple = 'Simple',
  Compound = 'Compound',
  None = 'None',
}

export enum CompoundingPeriod {
  Monthly = 'Monthly',
  Quarterly = 'Quarterly',
  Annual = 'Annual',
  None = 'None',
}

export enum ExitType {
  acquisition = 'Acquisition',
  IPO = 'IPO',
  soldBack = 'Sold Back',
  liquidated = 'Liquidated',
  tenderOfferSecondary = 'Tender Offer/Secondary',
}

export enum MiscPaymentType {
  capitalizedExpense = 'Capitalized Expense',
  cashReceived = 'Cash Received',
}

export enum OwnershipChangeReason {
  optionPoolTopUp = 'Option Pool Top-Up',
  correction = 'Correction',
}

export type RestructureShareData = {
  noOfShares: number;
  positionName: string;
  pricePerShare: number;
};

export interface ITransactionDataModel {
  accruedInterest?: number;
  acquirer?: CompanySearchResponse | null;
  aggregatedValue?: number | null;
  amount?: number;
  amountNormalized?: number;
  audits: Audit[];
  cashReceived?: number;
  coInvestors?: string[];
  companyId: number;
  compoundingFrequency?: string | null;
  conversionRatio?: number | null;
  convertedInterestAmount?: number;
  convertedPricePerShare?: number;
  convertedPrincipalAmount?: number;
  convertedRound?: string | null;
  convertedRoundId?: number | null;
  convertedSharesNo?: number;
  coupon?: number;
  // FIXME: MAGGIE-5488 type of couponType should be CouponType
  couponType?: string | null;
  createdAt?: string;
  createdBy?: string;
  currency: ICurrencyDataModel | null;
  currencyid: number;
  /** ISO code of `currencyid`; the API labels and prices the row by it. */
  currencyCode?: string;
  /** USD per unit of the row's currency; the API looks it up for the date when omitted. */
  fx_exchangeRate?: number | null;
  fx_currency?: string | null;
  currentFXRate?: number;
  currentInvestment?: number;
  deadLead: null;
  deadLeadId: null;
  discountRate: number | null;
  distributions?: number;
  escrowAmount: number;
  exchangedAmount: number | null;
  exchangedPricePerShare: number | null;
  exchangedRound: IRoundDataModel | null;
  exchangedRoundId: number | null;
  exchangedSharesNo: number | null;
  exchangeRate: number;
  exercisedWarrantsNo: number | null;
  exitOwnershipPercentage?: number;
  exitType: string | null;
  exitValuation: number | null;
  expirationDate?: Date;
  fmvBasis: null;
  fullyDilutedShares: number;
  fund?: IFundDataModel;
  fundAmount: number;
  fundId: number | null;
  familyId?: number | null;
  id: number;
  interestPaymentAmount: number | null;
  investmentAmount?: number | null;
  investmentRoundId?: number | null;
  leadInvestor?: string | null;
  ledgerId: null;
  // The api sets it on general ledger rows only, and those never carry a source.
  linkedConfirmedTransactionIds?: number[];
  /** A merged general ledger row's own ledger rows, each with a .2 id so a round edit changes that row alone. */
  ledgerRows?: ITransactionDataModel[];
  linkedNoteId: number | null;
  linkedOptionId: null;
  linkedTransactionId?: number | null;
  linkedWarrantIssuanceId?: number | null;
  maturityDate?: string | null;
  miscPaymentAmount?: number | null;
  miscPaymentType?: MiscPaymentType | null;
  newFullyDilutedShares?: number;
  newSharesIssued?: number | null;
  noOfShares?: number | null;
  noOfSharesNormalized?: number;
  notes?: null | string;
  optionsExercisedNo?: number | null;
  optionsExercisePrice?: number | null;
  optionsExpiryDate?: string | null;
  optionsIssuedNo?: number | null;
  optionsStrikePrice?: number | null;
  paymentInKind?: boolean;
  pikCadence?: string | null;
  pikEndDate?: string | null;
  pikStartDate?: string | null;
  position?: string;
  postMoneyValuation?: number | null;
  ppsShareClass: Record<string, number> | null;
  ppsSharesReceived?: null | number;
  pricePerShare: number | null;
  pricipalPaymentAmount?: number | null;
  principalAmount?: number | null;
  principalWriteOffAmount?: number | null;
  proceedsType?: string[] | null;
  quantity?: number;
  raiseAmount?: number;
  realizedInvestment?: number;
  reason: string | null;
  receivedAmount?: number;
  returnOfCapitalAmount?: number | null;
  receivedPricePerShare?: number | null;
  receivedRound: IRoundDataModel | null;
  receivedRoundId?: null | number;
  receivedSharesNo?: number | null;
  receivedSharesByRestructureId?: Record<string, RestructureShareData>;
  remainingShares?: number;
  restructureId?: null | string;
  round?: Partial<IFinanceRoundDataModel> | null; // Remove this from here
  roundId: number | null;
  roundNormalized?: IRoundDataModel | null;
  security: string | null;
  shares?: number;
  source?: string | null;
  exchangedSharesByRestructureId?: Record<string, number> | null;
  sharesReceived?: null | number;
  splitAcquirerIntoSeparateItem?: boolean;
  status: string | null;
  transactionDate: string;
  transactionStatus?: string;
  transactionTypeId: number;
  transType?: string;
  triggerPrice?: number | null;
  unrealizedInvestment?: number;
  updatedAt?: string;
  updatedBy?: string | null;
  valuationAmount?: number | null;
  valuationCap?: number | null;
  valuationDate?: null;
  valuationImpact?: boolean;
  verified?: boolean;
  warrantAcquiredPrice?: number;
  warrantExercisePrice?: number;
  warrantExpiryDate?: string | null;
  warrantsIssued?: number | null;
  warrantVested?: boolean | null;
}

export function createITransactionDataModel(
  overrides: Partial<ITransactionDataModel> = {}
): ITransactionDataModel {
  return {
    accruedInterest: undefined,
    acquirer: null,
    amount: undefined,
    amountNormalized: undefined,
    audits: [],
    cashReceived: undefined,
    companyId: 0,
    compoundingFrequency: null,
    conversionRatio: null,
    convertedInterestAmount: undefined,
    convertedPricePerShare: undefined,
    convertedPrincipalAmount: undefined,
    convertedRound: null,
    convertedRoundId: null,
    convertedSharesNo: undefined,
    coupon: undefined,
    couponType: null,
    currency: null,
    currencyid: 1,
    currentFXRate: undefined,
    currentInvestment: undefined,
    deadLead: null,
    deadLeadId: null,
    discountRate: null,
    distributions: 0,
    escrowAmount: 0,
    exchangedAmount: null,
    exchangedPricePerShare: null,
    exchangedRound: null,
    exchangedRoundId: null,
    exchangedSharesNo: null,
    exchangeRate: 1,
    exercisedWarrantsNo: null,
    exitOwnershipPercentage: undefined,
    exitType: null,
    exitValuation: null,
    expirationDate: new Date(),
    fmvBasis: null,
    fullyDilutedShares: 0,
    fundAmount: 0,
    fundId: null,
    familyId: null,
    id: 0,
    interestPaymentAmount: null,
    investmentAmount: 0,
    ledgerId: null,
    linkedNoteId: null,
    linkedOptionId: null,
    linkedTransactionId: null,
    linkedWarrantIssuanceId: null,
    maturityDate: null,
    miscPaymentAmount: null,
    miscPaymentType: null,
    newFullyDilutedShares: undefined,
    newSharesIssued: null,
    noOfShares: undefined,
    noOfSharesNormalized: undefined,
    notes: '',
    optionsExercisedNo: null,
    optionsExercisePrice: null,
    optionsExpiryDate: null,
    optionsIssuedNo: null,
    optionsStrikePrice: null,
    paymentInKind: false,
    pikCadence: null,
    pikEndDate: null,
    pikStartDate: null,
    position: '',
    postMoneyValuation: null,
    ppsSharesReceived: null,
    ppsShareClass: null,
    pricePerShare: 0,
    pricipalPaymentAmount: null,
    principalAmount: null,
    principalWriteOffAmount: undefined,
    proceedsType: null,
    quantity: undefined,
    raiseAmount: undefined,
    realizedInvestment: undefined,
    receivedAmount: undefined,
    returnOfCapitalAmount: undefined,
    receivedPricePerShare: null,
    receivedRound: null,
    receivedRoundId: null,
    receivedSharesNo: null,
    remainingShares: undefined,
    reason: null,
    restructureId: '',
    roundId: null,
    investmentRoundId: null,
    security: null,
    shares: undefined,
    sharesReceived: null,
    splitAcquirerIntoSeparateItem: false,
    status: null,
    transactionDate: '',
    transactionTypeId: 0,
    transType: '',
    triggerPrice: null,
    unrealizedInvestment: undefined,
    valuationAmount: undefined,
    valuationCap: null,
    valuationDate: null,
    valuationImpact: false,
    verified: false,
    warrantAcquiredPrice: undefined,
    warrantExpiryDate: null,
    warrantsIssued: null,
    warrantVested: null,
    ...overrides,
  };
}

export interface IExternalTransaction {
  amountNormalized: number;
  companyId: number;
  fundId: number | null;
  id: number;
  linkedTransactionId?: number | null;
  noOfSharesNormalized: number;
  position: string;
  pricePerShare: number;
  restructureId: string;
  roundId: number | null;
  roundNormalized?: IRoundDataModel | null;
  security: string | null;
  status: string;
  transactionDate: string;
  transactionTypeId: number;
}

export function createExternalTransactionDataModel(
  overrides?: Partial<IExternalTransaction>
): IExternalTransaction {
  return {
    id: 0,
    position: '',
    restructureId: '',
    fundId: null,
    companyId: 0,
    transactionDate: '',
    status: '',
    transactionTypeId: 0,
    roundId: null,
    noOfSharesNormalized: 0,
    pricePerShare: 0,
    amountNormalized: 0,
    roundNormalized: null,
    security: null,
    linkedTransactionId: null,
    ...overrides,
  };
}

export function isInternalTransaction(
  t: ITransactionDataModel | IExternalTransaction
): t is ITransactionDataModel {
  // some internal transactions don't have a linkedTransactionId (apparently those not returned when fetching all transactions)
  return t.id === t.linkedTransactionId || Number.isInteger(t.id); // won't be coerced to number
}

export function isRealizedCostTransactionType(transactionType: string) {
  return (
    transactionType === 'Investment - Return of Capital' ||
    transactionType === 'Investment - Write Off' ||
    transactionType === 'Investment - Capitalized Expenses'
  );
}

export function isReturnOfCapitalType(transactionType: string) {
  return transactionType === 'Investment - Return of Capital' || transactionType === 'Investment - Write Off';
}

export function isRealizedGainLoss(transaction: MetricsTransactionDataModel) {
  const transType = transaction?.transType?.toLowerCase();
  return transType === 'realized gain' || transType === 'realized loss';
}

export function isInvestmentCostTransaction(transaction: MetricsTransactionDataModel) {
  return transaction.transType === 'Investment - Cost';
}

export function isNoteOrSafeTransaction(t: MetricsTransactionDataModel) {
  const position = t.position?.toLocaleLowerCase();
  return (
    (t.transType === 'Investment - Cost' || t.transType === 'Investment - Restructure Out') &&
    t.quantity === 0 &&
    position &&
    (position.includes('note') ||
      position.includes('safe') ||
      position.includes('convertible') ||
      position.includes('simple agreement'))
  );
}
